import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasChunkError: boolean;
  hasOtherError: boolean;
  error?: Error;
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasChunkError: false, hasOtherError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if it's a chunk load error
    const isChunkError =
      error.message?.includes('Load failed') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('is not a valid JavaScript MIME type') || // Safari variant (CLASSELLA-1D)
      error.message?.includes("undefined is not an object (evaluating '"); // Safari variant: missing lazy export (CLASSELLA-1W)
    
    if (isChunkError) {
      return { hasChunkError: true, error };
    }
    // Let other errors also be caught so we show a fallback
    return { hasOtherError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: { 
        errorType: this.state.hasChunkError ? 'chunk-load-failure' : 'react-error'
      },
      extra: { componentStack: errorInfo.componentStack }
    });

    // For chunk load errors, attempt auto-refresh
    if (this.state.hasChunkError) {
      const lastRefresh = sessionStorage.getItem('lastChunkLoadRefresh');
      const now = Date.now();
      if (!lastRefresh || now - parseInt(lastRefresh) > 10000) {
        sessionStorage.setItem('lastChunkLoadRefresh', now.toString());
        window.location.reload();
        return;
      }
    }
  }

  render() {
    if (this.state.hasChunkError || this.state.hasOtherError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 mx-auto text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {this.state.hasChunkError ? 'Page Update Available' : 'Something Went Wrong'}
            </h2>
            <p className="text-gray-600 mb-6">
              {this.state.hasChunkError 
                ? 'A new version of Classella is available. Please refresh to continue.'
                : 'An unexpected error occurred. Please try refreshing the page.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
